'use client';
import React, { ReactElement, useEffect } from 'react';

import { Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogCloseButton } from '../../index';
import { AlertDialogButtonType, AlertDialogProps } from './alert-dialog.interfaces';

/**
 * Alert dialog component
 * This component is not supposed to be used directly
 * It is designed to be called by alert dialog service
 * @param props alert dialog component props
 */
export const AlertDialog = (props: AlertDialogProps): ReactElement => {
  const [open, setOpen] = React.useState(true);
  const { title, body, actions, onClose } = props;

  /**
   * Handle dialog destroy
   */
  useEffect(() => {
    return () => setOpen(false);
  }, []);

  /**
   * Close dialog
   * @param event mouse click event
   * @param value value of button choice
   */
  const handleClose = (event: React.MouseEvent<HTMLElement>, value: string) => {
    onClose && onClose(value as AlertDialogButtonType);
    open && setOpen(false);
  };

  const createAlertDialogButtonTemplate = (
    variant: 'text' | 'outlined' | 'contained',
    color: 'error' | 'inherit' | 'primary' | 'secondary' | 'tertiary' | 'success' | 'info' | 'warning',
    label: string,
    returnValue: AlertDialogButtonType
  ) => (
    <Button
      onClick={(e: React.MouseEvent<HTMLElement>) => handleClose(e, returnValue)}
      variant={variant}
      color={color}
      data-testid={`dialog-${returnValue}-button`}
    >
      {label}
    </Button>
  );

  const actionsTemplate = (
    <DialogActions disableSpacing={false}>
      {actions &&
        actions.buttonLeft &&
        createAlertDialogButtonTemplate(
          actions.buttonLeft?.variant || 'outlined',
          actions.buttonLeft?.color || 'secondary',
          actions.buttonLeft.label,
          actions.buttonLeft.type
        )}
      {actions &&
        actions.buttonMiddle &&
        createAlertDialogButtonTemplate(
          actions.buttonLeft?.variant || 'outlined',
          actions.buttonMiddle?.color || 'secondary',
          actions.buttonMiddle.label,
          actions.buttonMiddle.type
        )}
      {actions &&
        actions.buttonRight &&
        createAlertDialogButtonTemplate(
          actions.buttonLeft?.variant || 'contained',
          actions.buttonRight?.color || 'primary',
          actions.buttonRight.label,
          actions.buttonRight.type
        )}
    </DialogActions>
  );

  const alertDialogTemplate = (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogCloseButton onClick={(event) => handleClose(event, null)} />
      <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
      <DialogContent>{body}</DialogContent>
      {actions && actionsTemplate}
    </Dialog>
  );

  return alertDialogTemplate;
};

export default AlertDialog;

export * from './alert-dialog.interfaces';
